import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {skip, takeUntil} from 'rxjs/operators';
import {Ofwca} from '../../../shared/interface/ofwca';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {OfwcaService} from '../../../shared/services/ofwca.service';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {
  DICT_TYPE_TEMPLATE_TYPE,
  DictService,
  TEMPLATE_TYPE_AUTHORIZATION, TEMPLATE_TYPE_CONTRACT_WITH_EMPLOYEE, TEMPLATE_TYPE_OFWCA_APPLICATION,
  TEMPLATE_TYPE_STATEMENT
} from '../../../shared/services/dict.service';
import {HttpErrorResponse} from '@angular/common/http';
import {TemplateService} from '../../../shared/services/template.service';
import {DownloaderService} from '../../../shared/services/downloader.service';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {Uuid} from '../../../shared/interface/response/uuid';
import {IddTrainingService} from '../../../shared/services/idd-training.service';
import {IddTraining} from '../../../shared/interface/idd-training';
import {ListResponse} from '../../../shared/interface/response/list-response';
import {FormBuilder, FormControl} from '@angular/forms';
import {ChartType} from '../../idd-trainings/idd-training-chart/idd-training-chart.component';
import {ChangeRegionalDialogComponent} from '../change-regional-dialog/change-regional-dialog.component';
import {ChangePartnerDialogComponent} from '../change-partner-dialog/change-partner-dialog.component';
import {Document} from '../../../shared/interface/document';
import {DocumentService} from '../../../shared/services/document.service';
import {
  OfwcaChangeAccountNumberDialogComponent
} from "../ofwca-change-account-number-dialog/ofwca-change-account-number-dialog.component";
import {OfwcaChangeHiperioDataComponent} from "../ofwca-change-hiperio-data/ofwca-change-hiperio-data.component";
import {
  ConfirmDialogWithReasonComponent,
  ConfirmResult
} from "../../../shared/components/confirm-dialog-with-reason/confirm-dialog-with-reason.component";
import {AgentsProfileHiperioService} from "../../../shared/services/agents-profile-hiperio.service";
import {HiperioAgencyUnit} from "../../../shared/interface/hiperio-agency-unit";

@Component({
  selector: 'app-ofwca-details-view',
  templateUrl: './ofwca-details-view.component.html',
  styleUrls: ['./ofwca-details-view.component.scss']
})
export class OfwcaDetailsViewComponent implements OnInit, OnDestroy {
  @Input() ofwca: Observable<Ofwca>;
  @Output() modifyClicked = new EventEmitter<any>();
  @Output() ofwcaChanged = new EventEmitter<any>();

  ofwcaObject?: Ofwca;
  hiperioAgencyUnitObject?: HiperioAgencyUnit;
  userPermissions: ReplaySubject<UserPermissions>;
  generatingDocument = false;
  iddTrainings: IddTraining[] = [];
  iddTrainingControl: FormControl;
  chartType = ChartType.Horizontal;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private ofwcaService: OfwcaService,
    private auth: AuthService,
    private templateService: TemplateService,
    private downloader: DownloaderService,
    private dictService: DictService,
    private iddTrainingService: IddTrainingService,
    private fb: FormBuilder,
    private documentService: DocumentService,
    private agentsProfileHiperioService: AgentsProfileHiperioService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.iddTrainingControl = fb.control('');
  }

  ngOnInit(): void {
    const iddTrainingsSubject: ReplaySubject<ListResponse<IddTraining>>
      = new ReplaySubject<ListResponse<IddTraining>>(1);
    iddTrainingsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: ListResponse<IddTraining>) => {
        this.iddTrainings = response.items;

        let newestIddTraining: IddTraining = null;
        this.iddTrainings.forEach((iddTraining: IddTraining) => {
          if (null === newestIddTraining) {
            newestIddTraining = iddTraining;
            return;
          }

          if (newestIddTraining.year < iddTraining.year) {
            newestIddTraining = iddTraining;
          }
        });

        this.iddTrainingControl.setValue(newestIddTraining);
      });

    this.ofwca
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ofwca: Ofwca) => {
        this.ofwcaObject = ofwca;
        if (this.ofwcaObject.hiperioAgencyUnitsUuid) {
          this.agentsProfileHiperioService.get(this.ofwcaObject.hiperioAgencyUnitsUuid)
            .pipe()
            .subscribe((hiperioAgencyUnit: HiperioAgencyUnit) => {
                this.hiperioAgencyUnitObject = hiperioAgencyUnit;
              }
            );
        }

        this.userPermissions
        .pipe()
          .subscribe((permissions: UserPermissions) => {
            if (permissions.idd_training.read){
              this.iddTrainingService.loadNotCountedList(0, 1000, {
                ofwca: this.ofwcaObject.uuid
              }, iddTrainingsSubject);
            }
          });
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  modify(): void {
    this.modifyClicked.emit();
  }

  delete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć OFWCA?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.ofwcaService.deleteOfwca(this.ofwcaObject.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto OFWCA', '', {duration: 2000});
              this.router.navigate(['/ofwca']);
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }

  generateDocuments(): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_STATEMENT, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          ofwca: this.ofwcaObject.uuid,
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(result => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }

  generateAuthorizations(onlyNew: boolean): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_AUTHORIZATION, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          ofwca: this.ofwcaObject.uuid,
          onlyNew
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(() => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }
  generateContractWithEmployee(onlyNew: boolean): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_CONTRACT_WITH_EMPLOYEE, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          ofwca: this.ofwcaObject.uuid,
          onlyNew
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(() => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }

  changeRegional(): void {
    const dialogRef = this.dialog.open(ChangeRegionalDialogComponent, {
      width: '800px',
      data: {ofwcaObject: this.ofwcaObject},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.ofwcaChanged.emit();
    });
  }

  changePartner(): void {
    const dialogRef = this.dialog.open(ChangePartnerDialogComponent, {
      width: '80vw',
      maxHeight: '90vh',
      data: {ofwcaObject: this.ofwcaObject},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.ofwcaChanged.emit();
    });
  }

  generateApplication(): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_OFWCA_APPLICATION, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          ofwca: this.ofwcaObject.uuid,
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(() => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }

  downloadStatement(document: Document): void {
    this.documentService.downloadFile(document.uuid);
  }

  deleteStatement(document: Document): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć oświadczenie?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.documentService.deleteDocument(document.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto oświadczenie', '', {duration: 2000});
            this.ofwcaChanged.emit();
          });
      }
    });
  }
  accountNumber(): void {
    const dialogRef = this.dialog.open(OfwcaChangeAccountNumberDialogComponent, {
      width: '700px',
      data: {ofwcaObject: this.ofwcaObject},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.ofwcaChanged.emit();
    });
  }

  hiperiodata(): void {
    const dialogRef = this.dialog.open(OfwcaChangeHiperioDataComponent, {
      width: '700px',
      data: {ofwcaObject: this.ofwcaObject},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.ofwcaChanged.emit();
    });
  }

  blockOfwca(): void {
    const dialogRef = this.dialog.open(ConfirmDialogWithReasonComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz zablokować OFWCA we wszystkich TU? '},
    });

    dialogRef.afterClosed().subscribe((result: ConfirmResult) => {
      if (result.accepted) {
        this.ofwcaService.blockOfwca(this.ofwcaObject.uuid, result.reason)
          .subscribe(() => {
            this.snack.open('Pomyślnie zaktualizowano OFWCA.', '', {duration: 2000});
            this.ofwcaChanged.emit();
          });
      }
    });
  }
  unlockOfwca(): void {
    this.ofwcaService.unlockOfwca(this.ofwcaObject.uuid)
      .subscribe(() => {
        this.snack.open('Pomyślnie zaktualizowano OFWCA.', '', {duration: 2000});
        this.ofwcaChanged.emit();
      });
  }


  updateHiperioOfwca(status: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz zmienić stats konto ofwca w hiperio? '},
    });

    dialogRef.afterClosed().subscribe((result: ConfirmResult) => {
      if (result) {
        this.agentsProfileHiperioService.activationStatusHiperioOfwcaUpdate(this.ofwcaObject.uuid, status )
          .subscribe(() => {
            this.snack.open('Pomyślnie zaktualizowano_konto OFWCA.', '', {duration: 2000});
            this.ofwcaChanged.emit();
          });
      }
    });
  }

  // sendPassword(uuid: string): void {
  //   this.ofwcaService.sendPassword(uuid)
  //     .subscribe((response) => {
  //       this.snack.open('Hasło zostało wysłane',  '', {duration: 2000});
  //     }, (error: HttpErrorResponse) => {
  //       this.snack.open('Przy wysyłąniu hasła wystąpił błąd', error.status.toString(), {duration: 2000});
  //       if (error.status === 400) {
  //         this.snack.open(error.error.error.text, '400', {duration: 4000});
  //       }
  //     });
  //
  // }

}
